import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { canPerform } from "@vital/shared";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { MetricCard } from "../components/MetricCard";
import { PasswordConfirmModal } from "../components/PasswordConfirmModal";
import { OpsEmptyState, SectionHeader, InventorySkeleton } from "../components/ops-display";
import { InspectPanel } from "../components/InspectPanel";
import {
  ObservabilityAggregateFilterBar,
  ObservabilityAggregateInspectSummary,
  ObservabilityAggregateInventoryList,
  ObservabilityAlertFilterBar,
  ObservabilityAlertInspectSummary,
  ObservabilityAlertInventoryList,
  ObservabilityDrillFilterBar,
  ObservabilityDrillInspectSummary,
  ObservabilityDrillInventoryList,
  ObservabilityPrivacyBanner,
} from "../components/observability-inventory";
import {
  api,
  ApiError,
  type FleetDriftRow,
  type ObservabilityAggregateRow,
  type ObservabilityAlertEvent,
  type ObservabilityOpsSummary,
  type ObservabilityRestoreDrill,
} from "../lib/api";
import { DRIFT_KIND_LABELS } from "../lib/fleet-labels";
import {
  backupAgeDisplay,
  checkLabelFa,
  filterAndSortAggregates,
  filterAndSortAlerts,
  filterAndSortDrills,
  formatBytesFa,
  killSwitchLabelFa,
  readinessLabelFa,
  scrapeTokenDisplay,
  toAggregateListRow,
  webhookConfiguredDisplay,
  type AggregateSortKey,
  type AlertSortKey,
  type DrillSortKey,
  type ObservabilityAlertRow,
  type ObservabilityDrillRow,
} from "../lib/observability-ops";

type Tab = "overview" | "aggregates" | "alerts" | "backup";

export function ObservabilityPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const authInput = {
    platformRole: user?.platformRole ?? null,
    membershipRole: user?.membershipRole ?? null,
    legacyRole: user?.role,
  };
  const canRead = canPerform({ ...authInput, permission: "audit.read" });
  const isPlatformOps = user?.platformRole === "owner" || user?.platformRole === "operator";
  const isOwner = user?.platformRole === "owner";

  const tabParam = searchParams.get("tab");
  const tab: Tab =
    tabParam === "aggregates" || tabParam === "alerts" || tabParam === "backup"
      ? tabParam
      : "overview";

  function setTab(next: Tab) {
    const nextParams = new URLSearchParams(searchParams);
    if (next === "overview") nextParams.delete("tab");
    else nextParams.set("tab", next);
    setSearchParams(nextParams, { replace: true });
  }

  const [summary, setSummary] = useState<ObservabilityOpsSummary | null>(null);
  const [aggregates, setAggregates] = useState<ObservabilityAggregateRow[]>([]);
  const [disclaimerFa, setDisclaimerFa] = useState("");
  const [platformScope, setPlatformScope] = useState(false);
  const [alerts, setAlerts] = useState<ObservabilityAlertEvent[]>([]);
  const [drills, setDrills] = useState<ObservabilityRestoreDrill[]>([]);
  const [drift, setDrift] = useState<FleetDriftRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordPurpose, setPasswordPurpose] = useState<"drill" | "testAlert">("drill");
  const [password, setPassword] = useState("");
  const [confirming, setConfirming] = useState(false);

  const [alertQuery, setAlertQuery] = useState("");
  const [alertFilter, setAlertFilter] = useState("all");
  const [alertSort, setAlertSort] = useState<AlertSortKey>("fired");
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);

  const [aggQuery, setAggQuery] = useState("");
  const [aggFilter, setAggFilter] = useState("all");
  const [aggSort, setAggSort] = useState<AggregateSortKey>("bucket");
  const [selectedAggKey, setSelectedAggKey] = useState<string | null>(null);

  const [drillQuery, setDrillQuery] = useState("");
  const [drillFilter, setDrillFilter] = useState("all");
  const [drillSort, setDrillSort] = useState<DrillSortKey>("recorded");
  const [selectedDrillId, setSelectedDrillId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    setError(null);
    try {
      const sum = await api.getObservabilitySummary();
      setSummary(sum);
      setDisclaimerFa(sum.disclaimerFa);

      const agg = await api.listObservabilityAggregates({
        scope: isPlatformOps && platformScope ? "platform" : "organization",
      });
      setAggregates(agg.items);
      setDisclaimerFa(agg.disclaimerFa || sum.disclaimerFa);

      const driftRes = await api.listFleetDrift(
        isPlatformOps && platformScope ? "platform" : undefined,
      );
      setDrift(driftRes.items);

      if (isPlatformOps) {
        const [alertRes, drillRes] = await Promise.all([
          api.listObservabilityAlerts(),
          api.listRestoreDrills(),
        ]);
        setAlerts(alertRes.items);
        setDrills(drillRes.items);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری داشبورد عملیات ناموفق بود");
    } finally {
      setLoading(false);
    }
  }, [canRead, isPlatformOps, platformScope]);

  useEffect(() => {
    void load();
  }, [load]);

  const aggregateTotals = useMemo(() => {
    let total = 0;
    let sponsored = 0;
    let s5xx = 0;
    for (const row of aggregates) {
      total += row.count;
      if (row.sponsored === 1) sponsored += row.count;
      if (row.httpStatus === "5xx") s5xx += row.count;
    }
    return { total, sponsored, s5xx };
  }, [aggregates]);

  const alertRows = useMemo(() => alerts as ObservabilityAlertRow[], [alerts]);
  const filteredAlerts = useMemo(
    () =>
      filterAndSortAlerts(alertRows, {
        query: alertQuery,
        filter: alertFilter,
        sort: alertSort,
      }),
    [alertRows, alertQuery, alertFilter, alertSort],
  );
  const selectedAlert = useMemo(
    () => alertRows.find((a) => a.id === selectedAlertId) ?? null,
    [alertRows, selectedAlertId],
  );

  const aggRows = useMemo(() => aggregates.map(toAggregateListRow), [aggregates]);
  const filteredAggs = useMemo(
    () =>
      filterAndSortAggregates(aggRows, {
        query: aggQuery,
        filter: aggFilter,
        sort: aggSort,
      }),
    [aggRows, aggQuery, aggFilter, aggSort],
  );
  const selectedAgg = useMemo(
    () => filteredAggs.find((r) => r.key === selectedAggKey) ?? null,
    [filteredAggs, selectedAggKey],
  );

  const drillRows = useMemo(() => drills as ObservabilityDrillRow[], [drills]);
  const filteredDrills = useMemo(
    () =>
      filterAndSortDrills(drillRows, {
        query: drillQuery,
        filter: drillFilter,
        sort: drillSort,
      }),
    [drillRows, drillQuery, drillFilter, drillSort],
  );
  const selectedDrill = useMemo(
    () => drillRows.find((d) => d.id === selectedDrillId) ?? null,
    [drillRows, selectedDrillId],
  );

  const backupAge = backupAgeDisplay(summary?.gauges.backupAgeSeconds);
  const webhook = webhookConfiguredDisplay(Boolean(summary?.activation.webhookConfigured));
  const scrape = scrapeTokenDisplay(Boolean(summary?.activation.scrapeTokenConfigured));

  async function onEvaluateAlerts() {
    if (!isPlatformOps) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.evaluateObservabilityAlerts();
      setSuccess(`ارزیابی هشدارها انجام شد (${res.results.length} نتیجه).`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ارزیابی هشدار ناموفق بود");
    } finally {
      setBusy(false);
    }
  }

  async function onRunRetention() {
    if (!isOwner) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.runObservabilityRetention();
      setSuccess(
        `پاک‌سازی نگهداری اجرا شد — شمارنده: ${res.aggregatesDeleted}، ممیزی: ${res.auditDeleted}، هشدار: ${res.alertEventsDeleted}`,
      );
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "اجرای نگهداری ناموفق بود");
    } finally {
      setBusy(false);
    }
  }

  async function onConfirmPassword(e: FormEvent) {
    e.preventDefault();
    if (!isOwner) return;
    setConfirming(true);
    setError(null);
    setSuccess(null);
    try {
      if (passwordPurpose === "testAlert") {
        const res = await api.sendObservabilityTestAlert(password);
        setSuccess(
          res.status === "firing"
            ? "هشدار آزمایشی ارسال شد (بدون PII؛ مقصد در UI نمایش داده نمی‌شود)."
            : res.status === "skipped_no_destination"
              ? "هشدار آزمایشی ثبت شد — تحویل رد شد چون مقصد وب‌هوک پیکربندی نشده است."
              : `هشدار آزمایشی ثبت شد — وضعیت: ${res.status}`,
        );
      } else {
        const drill = await api.runRestoreDrill(password);
        setSuccess(
          drill.ok
            ? "تمرین بازیابی ایزوله موفق بود (پایگاه زنده تغییر نکرد)."
            : "تمرین بازیابی ثبت شد — نتیجه ناموفق.",
        );
      }
      setPasswordOpen(false);
      setPassword("");
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : passwordPurpose === "testAlert"
            ? "ارسال هشدار آزمایشی ناموفق بود"
            : "تمرین بازیابی ناموفق بود",
      );
    } finally {
      setConfirming(false);
    }
  }

  if (!canRead) {
    return (
      <div className="page">
        <PageHeader title="پایش عملیات" />
        <p className="banner banner--error" role="alert">
          مجوز audit.read لازم است.
        </p>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title="پایش عملیات"
        actions={
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void load()}
            disabled={loading}
          >
            تازه‌سازی
          </button>
        }
      />

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="form-success" role="status">
          {success}
        </p>
      )}

      <ObservabilityPrivacyBanner
        disclaimerFa={disclaimerFa || summary?.disclaimerFa}
      />

      <div className="tab-row" role="tablist" style={{ flexWrap: "wrap" }}>
        {(
          [
            ["overview", "خلاصه"],
            ["aggregates", "شمارنده‌ها"],
            ["alerts", "هشدارها"],
            ["backup", "پشتیبان و نگهداری"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            className={`tab ${tab === id ? "tab--active" : ""}`}
            aria-selected={tab === id}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
        {isPlatformOps && (
          <button
            type="button"
            className={`tab ${platformScope ? "tab--active" : ""}`}
            aria-pressed={platformScope}
            onClick={() => setPlatformScope((v) => !v)}
          >
            {platformScope ? "نمای سکو" : "نمای سازمان"}
          </button>
        )}
      </div>

      {loading ? (
        <InventorySkeleton />
      ) : tab === "overview" ? (
        <div className="page-stack">
          <div className="metric-grid">
            <MetricCard
              label="آمادگی CMS"
              value={readinessLabelFa(summary?.readiness.status)}
              sublabel={
                summary
                  ? `postgres: ${checkLabelFa(summary.readiness.checks.postgres)} · uploads: ${checkLabelFa(summary.readiness.checks.uploads)} · backup: ${checkLabelFa(summary.readiness.checks.backup)}`
                  : undefined
              }
              loading={loading}
            />
            <MetricCard
              label="سن پشتیبان"
              value={backupAge.label}
              sublabel={backupAge.detail ?? "از آخرین فایل vital-portal-*.db"}
              loading={loading}
            />
            <MetricCard
              label="کلید اضطراری"
              value={killSwitchLabelFa(summary?.gauges.killSwitchActive)}
              sublabel="فقط نمایش محتوا — بدون تغییر ورود"
              loading={loading}
            />
            <MetricCard
              label="انحراف ناوگان"
              value={summary?.gauges.fleetDriftSites ?? 0}
              sublabel="ثبت‌شده توسط اپراتور (نه پروب زنده)"
              loading={loading}
            />
            <MetricCard
              label="مصرف فضای آپلود"
              value={
                summary?.gauges.storageUsedPercent != null
                  ? `${summary.gauges.storageUsedPercent}%`
                  : "—"
              }
              sublabel={`آزاد: ${formatBytesFa(summary?.gauges.uploadBytesFree)}`}
              loading={loading}
            />
            <MetricCard
              label="شمارنده ۲۴ساعته"
              value={aggregateTotals.total}
              sublabel={`آگهی‌دار: ${aggregateTotals.sponsored} · ۵xx: ${aggregateTotals.s5xx}`}
              loading={loading}
            />
            <MetricCard
              label="وب‌هوک هشدار"
              value={webhook.label}
              sublabel={webhook.detail}
              loading={loading}
            />
            <MetricCard
              label="اسکرپر /metrics"
              value={scrape.label}
              sublabel={scrape.detail}
              loading={loading}
            />
          </div>

          <Card>
            <SectionHeader
              title="انحراف ناوگان (خلاصه)"
              actionLabel="جزئیات ناوگان"
              actionTo="/fleet?tab=drift"
            />
            {drift.length === 0 ? (
              <OpsEmptyState
                title="انحراف بازی برای این محدوده ثبت نشده است"
                actionLabel="ناوگان"
                actionTo="/fleet?tab=drift"
              />
            ) : (
              <div className="sites-inventory-table-wrap" role="region" aria-label="خلاصه انحراف">
                <table className="data-table content-inventory-table">
                  <thead>
                    <tr>
                      <th>سایت</th>
                      {platformScope && <th>سازمان</th>}
                      <th>نوع</th>
                      <th>توضیح</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drift.slice(0, 12).map((row) => (
                      <tr key={`${row.siteId}-${row.driftKind}`}>
                        <td>
                          <bdi dir="ltr" className="ltr-isolate latin-digits">
                            {row.siteCode}
                          </bdi>
                        </td>
                        {platformScope && (
                          <td>
                            <bdi dir="ltr" className="ltr-isolate latin-digits">
                              {row.organizationCode ?? "—"}
                            </bdi>
                          </td>
                        )}
                        <td>{DRIFT_KIND_LABELS[row.driftKind] ?? row.driftKind}</td>
                        <td>{row.driftLabel}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p style={{ marginBlockStart: "0.75rem" }}>
              <Link to="/fleet?tab=drift" className="btn btn--ghost btn--sm">
                جزئیات ناوگان
              </Link>
            </p>
          </Card>
        </div>
      ) : tab === "aggregates" ? (
        <div className="page-stack page-stack--inventory">
          <div
            className={`content-ops-layout${selectedAgg ? " content-ops-layout--inspect-open" : ""}`}
            data-vt-content-ops
            data-vt-inspect-open={selectedAgg ? "true" : "false"}
          >
            <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
              <SectionHeader title="شمارنده‌های ساعتی محتوا" />
              <ObservabilityAggregateFilterBar
                filter={aggFilter}
                onFilterChange={setAggFilter}
                query={aggQuery}
                onQueryChange={setAggQuery}
                sort={aggSort}
                onSortChange={setAggSort}
              />
              {aggregates.length === 0 ? (
                <OpsEmptyState title="در بازه انتخاب‌شده سطری نیست" />
              ) : filteredAggs.length === 0 ? (
                <OpsEmptyState title="موردی در این فیلتر نیست" />
              ) : (
                <ObservabilityAggregateInventoryList
                  rows={filteredAggs}
                  selectedKey={selectedAggKey}
                  onSelect={setSelectedAggKey}
                  showOrg={platformScope}
                />
              )}
            </Card>
            {selectedAgg && (
              <InspectPanel title="جزئیات شمارنده" className="content-inspect-panel">
                <ObservabilityAggregateInspectSummary item={selectedAgg} />
              </InspectPanel>
            )}
          </div>
        </div>
      ) : tab === "alerts" ? (
        <div className="page-stack page-stack--inventory">
          <div
            className={`content-ops-layout${selectedAlert ? " content-ops-layout--inspect-open" : ""}`}
            data-vt-content-ops
            data-vt-inspect-open={selectedAlert ? "true" : "false"}
          >
            <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
              <SectionHeader
                title="تاریخچه هشدارها"
                action={
                  isPlatformOps ? (
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="btn btn--primary btn--sm"
                        disabled={busy}
                        onClick={() => void onEvaluateAlerts()}
                      >
                        ارزیابی اکنون
                      </button>
                      {isOwner && (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => {
                            setPasswordPurpose("testAlert");
                            setPassword("");
                            setPasswordOpen(true);
                          }}
                        >
                          هشدار آزمایشی
                        </button>
                      )}
                    </div>
                  ) : undefined
                }
              />
              {!isPlatformOps ? (
                <OpsEmptyState title="تاریخچه هشدار فقط برای مالک/اپراتور سکو" />
              ) : (
                <>
                  <ObservabilityAlertFilterBar
                    filter={alertFilter}
                    onFilterChange={setAlertFilter}
                    query={alertQuery}
                    onQueryChange={setAlertQuery}
                    sort={alertSort}
                    onSortChange={setAlertSort}
                  />
                  {alerts.length === 0 ? (
                    <OpsEmptyState title="رویداد هشداری ثبت نشده است" />
                  ) : filteredAlerts.length === 0 ? (
                    <OpsEmptyState title="موردی در این فیلتر نیست" />
                  ) : (
                    <ObservabilityAlertInventoryList
                      rows={filteredAlerts}
                      selectedId={selectedAlertId}
                      onSelect={setSelectedAlertId}
                    />
                  )}
                </>
              )}
              <p className="card__muted" style={{ marginBlockStart: "0.75rem" }}>
                مقصد وب‌هوک فقط از متغیر محیطی سکو خوانده می‌شود و در این صفحه نمایش داده نمی‌شود.
                وضعیت «بدون مقصد» یعنی تحویل رد شده است — نه پیکربندی زنده از UI.
              </p>
            </Card>
            {selectedAlert && (
              <InspectPanel title="جزئیات هشدار" className="content-inspect-panel">
                <ObservabilityAlertInspectSummary item={selectedAlert} />
              </InspectPanel>
            )}
          </div>
        </div>
      ) : tab === "backup" ? (
        <div className="page-stack page-stack--inventory">
          <Card title="وضعیت نگهداری">
            <div className="metric-grid">
              <MetricCard
                label="شمارنده‌ها"
                value={`${summary?.retention.aggregateRetentionDays ?? "—"} روز`}
                loading={loading}
              />
              <MetricCard
                label="ممیزی"
                value={`${summary?.retention.auditRetentionDays ?? "—"} روز`}
                loading={loading}
              />
              <MetricCard
                label="رویداد هشدار"
                value={`${summary?.retention.alertEventRetentionDays ?? "—"} روز`}
                loading={loading}
              />
            </div>
            {isOwner && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                style={{ marginBlockStart: "0.75rem" }}
                disabled={busy}
                onClick={() => void onRunRetention()}
              >
                اجرای پاک‌سازی نگهداری
              </button>
            )}
          </Card>

          <div
            className={`content-ops-layout${selectedDrill ? " content-ops-layout--inspect-open" : ""}`}
            data-vt-content-ops
            data-vt-inspect-open={selectedDrill ? "true" : "false"}
          >
            <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
              <SectionHeader
                title="تمرین بازیابی ایزوله"
                action={
                  isOwner ? (
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      onClick={() => {
                        setPasswordPurpose("drill");
                        setPassword("");
                        setPasswordOpen(true);
                      }}
                    >
                      اجرای تمرین
                    </button>
                  ) : undefined
                }
              />
              <p>
                آخرین موفقیت:{" "}
                <strong>
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {summary?.restoreDrill.lastSuccessAt
                      ? new Date(summary.restoreDrill.lastSuccessAt).toLocaleString("fa-AF", {
                          timeZone: "Asia/Kabul",
                        })
                      : "—"}
                  </bdi>
                </strong>
              </p>
              <p className="card__muted">
                سن پشتیبان در خلاصه: {backupAge.label}
                {backupAge.detail ? ` — ${backupAge.detail}` : ""}
              </p>
              {!isPlatformOps ? (
                <OpsEmptyState title="شواهد تمرین فقط برای نقش‌های سکو" />
              ) : (
                <>
                  <ObservabilityDrillFilterBar
                    filter={drillFilter}
                    onFilterChange={setDrillFilter}
                    query={drillQuery}
                    onQueryChange={setDrillQuery}
                    sort={drillSort}
                    onSortChange={setDrillSort}
                  />
                  {drills.length === 0 ? (
                    <OpsEmptyState title="هنوز تمرین ثبت نشده است" />
                  ) : filteredDrills.length === 0 ? (
                    <OpsEmptyState title="موردی در این فیلتر نیست" />
                  ) : (
                    <ObservabilityDrillInventoryList
                      rows={filteredDrills}
                      selectedId={selectedDrillId}
                      onSelect={setSelectedDrillId}
                    />
                  )}
                </>
              )}
            </Card>
            {selectedDrill && (
              <InspectPanel title="جزئیات تمرین" className="content-inspect-panel">
                <ObservabilityDrillInspectSummary item={selectedDrill} />
              </InspectPanel>
            )}
          </div>
        </div>
      ) : null}

      {passwordOpen && (
        <PasswordConfirmModal
          title={
            passwordPurpose === "testAlert"
              ? "تأیید هشدار آزمایشی عملیات"
              : "تأیید تمرین بازیابی ایزوله"
          }
          description={
            passwordPurpose === "testAlert"
              ? "یک هشدار privacy-safe به مقصد پیکربندی‌شده ارسال می‌شود. URL یا رمز مقصد نمایش داده نمی‌شود."
              : "پایگاه تولید بازنویسی نمی‌شود. فقط کپی موقت و ثبت شواهد موفقیت/شکست."
          }
          password={password}
          onPasswordChange={setPassword}
          confirming={confirming}
          confirmLabel={passwordPurpose === "testAlert" ? "ارسال آزمایشی" : "اجرای تمرین"}
          onCancel={() => {
            setPasswordOpen(false);
            setPassword("");
          }}
          onConfirm={onConfirmPassword}
        />
      )}
    </div>
  );
}
